import { type VariantProps, cv } from "@suse-o11y/class-variance";
import React from "react";

import { ZIndexBoundary } from "~/components/z-index-boundary";

import styles from "./_select.module.css";

const LazyPortal = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Portal } = await import("@radix-ui/react-select");

	return { default: Portal };
});

const LazyViewport = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Viewport } = await import("@radix-ui/react-select");

	return { default: Viewport };
});

const LazyContent = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Content } = await import("@radix-ui/react-select");

	return { default: Content };
});

const variants = cv(styles.content, {
	variants: {
		width: {
			auto: null,
			"trigger-max": styles.widthMax,
			"trigger-min": styles.widthMin,
			"trigger-same": styles.widthSame,
		},
	},

	defaultVariants: {
		width: "trigger-same",
	},
});

export interface SelectContentProps
	extends VariantProps<typeof variants>,
		React.ComponentPropsWithoutRef<typeof LazyContent> {}

export const SelectContent = React.forwardRef<React.ElementRef<typeof LazyContent>, SelectContentProps>(
	({ className, position = "popper", width, style, children, ...props }, ref) => (
		<React.Suspense fallback={null}>
			<LazyPortal>
				<ZIndexBoundary>
					{(zIndex = 50) => (
						<LazyContent
							arrowPadding={4}
							asChild
							className={variants({ width, className })}
							collisionPadding={8}
							data-position={position}
							position={position}
							ref={ref} // avoid sticking to the edges of the boundaries
							sideOffset={4} // avoid rendering the arrow on the rounded corners
							style={{ zIndex: zIndex + 1, ...style }} // distance from the trigger
							{...props}
						>
							<LazyViewport className={styles.viewport}>{children}</LazyViewport>
						</LazyContent>
					)}
				</ZIndexBoundary>
			</LazyPortal>
		</React.Suspense>
	),
);

if (import.meta.env.DEV) {
	SelectContent.displayName = "SelectContent";
}
